import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../../context/AuthContext";

import xynewhitelogo from "./assets/xynewhitelogo.png";

const CHAT_URL = "https://techfest.org/api/chat/";

// Cleans up mojibake (mis-decoded UTF-8 -> Latin-1 artifacts) and
// strips citation markers like [0], [1], [12] from streamed text.
function sanitizeText(text) {
    if (!text) return text;

    return (
        text
            // Common mis-decoded punctuation sequences
            .replace(/â€™/g, "\u2019") // ’
            .replace(/â€˜/g, "\u2018") // ‘
            .replace(/â€œ/g, "\u201c") // “
            .replace(/â€\u009d/g, "\u201d") // ” (some servers send this variant)
            .replace(/â€\?/g, "\u201d") // ” fallback
            .replace(/â€"/g, "\u2013") // –
            .replace(/â€"/g, "\u2014") // —
            .replace(/â€¦/g, "\u2026") // …
            .replace(/â€¢/g, "\u2022") // •
            .replace(/Â°/g, "\u00b0") // °
            .replace(/Â®/g, "\u00ae") // ®
            .replace(/Â©/g, "\u00a9") // ©
            .replace(/Â /g, " ")
            .replace(/Â/g, "")
            // Any remaining stray "â" + junk byte sequences we haven't named
            .replace(/â[\u0080-\u00BF]{1,2}/g, "")
            // Citation markers like [0], [1], [23]
            .replace(/\[\d+\]/g, "")
            // Collapse any double spaces left behind by the removals above
            .replace(/[ \t]{2,}/g, " ")
            .trim()
    );
}

// Render every link as a clickable, new-tab link.
// (remark-gfm below also auto-links bare URLs like https://example.com)
const markdownComponents = {
    a: ({ node, href, children, ...props }) => (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
            {children}
        </a>
    ),
};

const styles = `
  .tf-chatbot-widget {
    display: flex;
    flex-direction: column;
    width: 360px;
    max-width: 100%;
    height: 520px;
    box-sizing: border-box;
    border-radius: 12px;
    border: 1px solid rgba(255, 59, 59, 0.28);
    background: linear-gradient(180deg, #140707 0%, #0a0505 100%);
    color: #f1e6e6;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    overflow: hidden;
    position: relative;
    overscroll-behavior: contain;
  }

  .tf-chatbot-widget *,
  .tf-chatbot-widget *::before,
  .tf-chatbot-widget *::after {
    box-sizing: border-box;
  }

  .tf-chatbot-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    font-weight: 600;
    font-size: 15px;
    letter-spacing: 0.2px;
    background: linear-gradient(135deg, rgba(200,16,46,0.35), rgba(255,59,59,0.08));
    border-bottom: 1px solid rgba(255, 59, 59, 0.22);
    position: relative;
    backdrop-filter: blur(6px);
  }

  .tf-chatbot-header::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 1px;
    background: linear-gradient(90deg, transparent, #ff3b3b, transparent);
    opacity: 0.8;
  }

  .tf-chatbot-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tf-chatbot-close {
    background: none;
    border: none;
    color: #b09a9a;
    font-size: 14px;
    cursor: pointer;
    padding: 4px 7px;
    border-radius: 6px;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .tf-chatbot-close:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }

  .tf-chatbot-messages {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,59,59,0.4) transparent;
  }

  .tf-chatbot-messages::-webkit-scrollbar {
    width: 6px;
  }

  .tf-chatbot-messages::-webkit-scrollbar-thumb {
    background: rgba(255, 59, 59, 0.4);
    border-radius: 10px;
  }

  .tf-chatbot-empty {
    margin: auto;
    color: #fff;
    font-size: 14px;
    text-align: center;
    line-height: 1.5;
  }

  .tf-chatbot-msg {
    display: flex;
    max-width: 85%;
    min-width: 0;
    animation: tfMsgIn 0.25s ease-out;
  }

  @keyframes tfMsgIn {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .tf-chatbot-msg--user {
    align-self: flex-end;
  }

  .tf-chatbot-msg--bot {
    align-self: flex-start;
  }

  .tf-chatbot-bubble {
    min-width: 0;
    max-width: 100%;
    padding: 10px 14px;
    border-radius: 14px;
    font-size: 14px;
    line-height: 1.5;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .tf-chatbot-msg--user .tf-chatbot-bubble {
    background: linear-gradient(135deg, #b30012, #ff3b3b);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 10px rgba(200, 16, 46, 0.4);
  }

  .tf-chatbot-msg--bot .tf-chatbot-bubble {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 90, 90, 0.14);
    backdrop-filter: blur(8px);
    border-bottom-left-radius: 4px;
  }

  /* Keep all markdown output inside the bubble — no horizontal scroll */
  .tf-chatbot-bubble p {
    margin: 0 0 6px 0;
  }

  .tf-chatbot-bubble p:last-child {
    margin-bottom: 0;
  }

  .tf-chatbot-bubble pre {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    overflow-x: hidden;
    max-width: 100%;
    margin: 6px 0;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(255, 59, 59, 0.15);
  }

  .tf-chatbot-bubble code {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    font-size: 12.5px;
    background: rgba(255, 59, 59, 0.12);
    padding: 1px 4px;
    border-radius: 4px;
  }

  .tf-chatbot-bubble pre code {
    background: none;
    padding: 0;
  }

  /* Highlighted, clickable links */
  .tf-chatbot-bubble a {
    color: #ff9a9a;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    background: rgba(255, 59, 59, 0.14);
    padding: 0 3px;
    border-radius: 4px;
    cursor: pointer;
    overflow-wrap: anywhere;
    word-break: break-all;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .tf-chatbot-bubble a:hover {
    color: #fff;
    background: rgba(255, 59, 59, 0.4);
  }

  .tf-chatbot-msg--user .tf-chatbot-bubble a {
    color: #fff;
    background: rgba(255, 255, 255, 0.22);
  }

  .tf-chatbot-msg--user .tf-chatbot-bubble a:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  .tf-chatbot-bubble img {
    max-width: 100%;
    height: auto;
  }

  .tf-chatbot-bubble table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 12.5px;
  }

  .tf-chatbot-bubble th,
  .tf-chatbot-bubble td {
    border: 1px solid rgba(255, 90, 90, 0.2);
    padding: 4px 6px;
    overflow-wrap: anywhere;
    word-break: break-word;
    text-align: left;
    vertical-align: top;
  }

  .tf-chatbot-bubble ul,
  .tf-chatbot-bubble ol {
    margin: 4px 0;
    padding-left: 20px;
  }

  .tf-chatbot-bubble blockquote {
    margin: 6px 0;
    padding-left: 10px;
    border-left: 3px solid #ff3b3b;
    color: #cdb9b9;
  }

  .tf-chatbot-sources {
    margin-top: 8px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    font-size: 11px;
    color: #9a8585;
  }

  .tf-chatbot-error {
    padding: 8px 14px;
    font-size: 13px;
    color: #ff9494;
    background: rgba(255, 40, 40, 0.1);
    border-top: 1px solid rgba(255, 60, 60, 0.3);
  }

  .tf-chatbot-input-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border-top: 1px solid rgba(255, 59, 59, 0.18);
    background: rgba(255, 255, 255, 0.02);
  }

  .tf-chatbot-input {
    flex: 1;
    min-width: 0;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);
    color: #f1e6e6;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .tf-chatbot-input::placeholder {
    color: #7d6a6a;
  }

  .tf-chatbot-input:focus {
    border-color: #ff3b3b;
    box-shadow: 0 0 0 3px rgba(255, 59, 59, 0.18);
  }

  .tf-chatbot-send {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, #b30012, #ff3b3b);
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(200, 16, 46, 0.45);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
  }

  .tf-chatbot-send:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(255, 59, 59, 0.55);
  }

  .tf-chatbot-send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export default function Chatbot({ onClose }) {
    const { currentUser, authFetch } = useAuth();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [error, setError] = useState("");
    const widgetRef = useRef(null);
    const messagesRef = useRef(null);

    // ---- Load the last 10 exchanges for this user when the widget opens ----
    useEffect(() => {
        if (!currentUser) return;

        let cancelled = false;

        const loadHistory = async () => {
            setHistoryLoading(true);
            try {
                const res = await authFetch(CHAT_URL, { method: "GET" });
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;

                const history = (data.messages || []).map((m) => ({
                    role: m.role,
                    text: sanitizeText(m.text),
                    sources: m.sources || [],
                }));

                // Don't clobber anything the user already typed while this loaded
                setMessages((prev) => (prev.length === 0 ? history : prev));
            } catch {
                // History is a nicety; fail silently.
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        };

        loadHistory();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // ---- Auto-scroll to the bottom whenever messages change (incl. every streamed token) ----
    useEffect(() => {
        const el = messagesRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, historyLoading]);

    // Stop page scroll from leaking out of the chatbot. `overscroll-behavior`
    // handles most cases, but it doesn't help when the list isn't scrollable
    // (or when the wheel happens over the header/input), so block those here.
    useEffect(() => {
        const widget = widgetRef.current;
        if (!widget) return;

        const onWheel = (e) => {
            const msgs = messagesRef.current;
            if (msgs && msgs.contains(e.target)) {
                const { scrollTop, scrollHeight, clientHeight } = msgs;
                const atTop = scrollTop <= 0;
                const atBottom = scrollTop + clientHeight >= scrollHeight - 1;
                if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
                    e.preventDefault();
                }
            } else {
                e.preventDefault();
            }
        };

        widget.addEventListener("wheel", onWheel, { passive: false });
        return () => widget.removeEventListener("wheel", onWheel);
    }, []);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        if (!currentUser) {
            setError("Please sign in to use the chatbot.");
            return;
        }

        setMessages((prev) => [...prev, { role: "user", text }]);
        setInput("");
        setLoading(true);
        setError("");

        let botText = "";
        setMessages((prev) => [
            ...prev,
            { role: "bot", text: "", sources: [] },
        ]);

        try {
            // No session_id: the server maps the signed-in account to its single
            // conversation, so context is preserved across visits and devices.
            const res = await authFetch(CHAT_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: text }),
            });

            if (res.status === 401) throw new Error("UNAUTHORIZED");
            if (res.status === 429) throw new Error("RATE_LIMITED");
            if (!res.ok) throw new Error("REQUEST_FAILED");

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                const events = buffer.split("\n\n");
                buffer = events.pop();

                for (const evt of events) {
                    const line = evt.replace(/^data:\s*/, "");
                    if (!line) continue;

                    let parsed;
                    try {
                        parsed = JSON.parse(line);
                    } catch {
                        continue;
                    }

                    if (parsed.type === "token") {
                        botText += parsed.text;
                        const cleaned = sanitizeText(botText);
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: "bot",
                                text: cleaned,
                                sources: [],
                            };
                            return updated;
                        });
                    } else if (parsed.type === "done") {
                        const cleaned = sanitizeText(botText);
                        setMessages((prev) => {
                            const updated = [...prev];
                            updated[updated.length - 1] = {
                                role: "bot",
                                text: cleaned,
                                sources: parsed.sources || [],
                            };
                            return updated;
                        });
                    } else if (parsed.type === "error") {
                        throw new Error(parsed.message || "UPSTREAM_ERROR");
                    }
                }
            }
        } catch (err) {
            if (err.message === "RATE_LIMITED") {
                setError(
                    "You've hit today's message limit — please try again tomorrow.",
                );
            } else if (err.message === "UNAUTHORIZED") {
                setError("Please sign in to use the chatbot.");
            } else {
                setError("Something went wrong. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="tf-chatbot-widget" ref={widgetRef}>
            <style>{styles}</style>

            <div className="tf-chatbot-header">
                <div className="tf-chatbot-header-title">
                    Ask{" "}
                    <img
                        src={xynewhitelogo}
                        alt="XYNE Logo"
                        style={{
                            height: "0.8em",
                            verticalAlign: "middle",
                            display: "inline-block",
                        }}
                    />
                </div>
                {onClose && (
                    <button
                        className="tf-chatbot-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div className="tf-chatbot-messages" ref={messagesRef}>
                {messages.length === 0 && !historyLoading && (
                    <div className="tf-chatbot-empty">
                        Powered by{" "}
                        <img
                            src={xynewhitelogo}
                            alt="XYNE Logo"
                            style={{
                                height: "1em",
                                verticalAlign: "middle",
                                display: "inline-block",
                            }}
                        />
                    </div>
                )}
                {messages.length === 0 && historyLoading && (
                    <div className="tf-chatbot-empty">Loading your chat…</div>
                )}
                {messages.map((m, i) => (
                    <div
                        key={i}
                        className={`tf-chatbot-msg tf-chatbot-msg--${m.role}`}
                    >
                        <div className="tf-chatbot-bubble">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                            >
                                {m.text || "…"}
                            </ReactMarkdown>
                            {/* {m.sources && m.sources.length > 0 && (
                                <div className="tf-chatbot-sources">
                                    Sources: {m.sources.join(", ")}
                                </div>
                            )} */}
                        </div>
                    </div>
                ))}
            </div>

            {error && <div className="tf-chatbot-error">{error}</div>}

            <div className="tf-chatbot-input-row">
                <input
                    className="tf-chatbot-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Ask about Techfest..."
                    disabled={loading}
                />
                <button
                    className="tf-chatbot-send"
                    onClick={sendMessage}
                    disabled={loading}
                    aria-label="Send message"
                >
                    {loading ? "…" : "➤"}
                </button>
            </div>
        </div>
    );
}
